import styled from "@emotion/styled";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { PageWithTitle } from "src/Common";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink } from "react-router-dom";
import {
  PREDICTION_MANAGER_HEALTH,
  PREDICTION_CALCULATOR_HEALTH,
  PREDICTION_WS_HEALTH,
} from "src/Api/endpoints";
import { useQuery } from "react-query";
import {
  getPredictionManagerHealth,
  getPredictionCalculatorHealth,
  getPredictionWsHealth,
} from "src/Api";

const REFETCH_INTERVAL = 15000;

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function getStatusColor(
  status?: string,
  isError?: boolean
): "success" | "error" | "warning" | "default" {
  if (isError) return "error";
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s === "ok" || s === "healthy" || s === "running") return "success";
  if (s === "warming_up" || s === "waiting_for_data" || s === "degraded")
    return "warning";
  return "error";
}

function getStatusLabel(status?: string, isError?: boolean): string {
  if (isError) return "Unreachable";
  if (!status) return "Unknown";
  const s = status.toLowerCase();
  if (s === "ok" || s === "healthy" || s === "running") return "Healthy";
  if (s === "warming_up") return "Warming Up";
  if (s === "waiting_for_data") return "Waiting for Data";
  if (s === "no_market_data") return "No Market Data";
  if (s === "degraded") return "Degraded";
  return "Unhealthy";
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

export const PredictionsStatus = () => {
  const {
    data: pairManager,
    isLoading: pmLoading,
    isError: pmError,
  } = useQuery(PREDICTION_MANAGER_HEALTH, getPredictionManagerHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  const {
    data: calculator,
    isLoading: calcLoading,
    isError: calcError,
  } = useQuery(PREDICTION_CALCULATOR_HEALTH, getPredictionCalculatorHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  const {
    data: predWs,
    isLoading: wsLoading,
    isError: wsError,
  } = useQuery(PREDICTION_WS_HEALTH, getPredictionWsHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  return (
    <PageWithTitle
      title={
        <Stack spacing={2}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component={RouterLink}
              underline="hover"
              key="1"
              color="inherit"
              to="/arbitrage"
            >
              Arbitrage
            </Link>
            <Typography key="2" color="text.primary">
              Predictions Status
            </Typography>
          </Breadcrumbs>
        </Stack>
      }
      style={{ padding: "2rem 5%" }}
    >
      <Grid container spacing={3}>
        {/* Prediction Pair Manager */}
        <Grid item xs={12} sm={6} md={4}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Prediction Pair Manager</ServiceName>
                {pmLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(pairManager?.status, pmError)}
                    color={getStatusColor(pairManager?.status, pmError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!pmLoading && !pmError && pairManager && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>Total Pairs</MetricLabel>
                    <MetricValue>
                      {(pairManager.stats?.total ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Active</MetricLabel>
                    <MetricValue>
                      {(pairManager.stats?.active ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Pending</MetricLabel>
                    <MetricValue>
                      {(pairManager.stats?.pending ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Keyword Pending</MetricLabel>
                    <MetricValue>
                      {(pairManager.stats?.keywordPending ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Expired</MetricLabel>
                    <MetricValue>
                      {(pairManager.stats?.expired ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {pmError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {pairManager?.timestamp
                  ? `Last checked: ${new Date(pairManager.timestamp).toLocaleTimeString()}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>

        {/* Prediction Calculator */}
        <Grid item xs={12} sm={6} md={4}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Prediction Calculator</ServiceName>
                {calcLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(calculator?.status, calcError)}
                    color={getStatusColor(calculator?.status, calcError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!calcLoading && !calcError && calculator && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>Instance</MetricLabel>
                    <MetricValue>
                      {calculator.instance_id ?? "-"}/{calculator.total_instances ?? "-"}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Active Pairs</MetricLabel>
                    <MetricValue>
                      {(calculator.cache?.active_pairs ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Market Data Keys</MetricLabel>
                    <MetricValue>
                      {(calculator.cache?.market_data_keys ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Calc Runs</MetricLabel>
                    <MetricValue>
                      {(calculator.calculation?.total_runs ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Profitable</MetricLabel>
                    <MetricValue>
                      {(calculator.calculation?.profitable_found ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Active Opps</MetricLabel>
                    <MetricValue>
                      {(calculator.publishing?.active_opportunities ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {calcError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {calculator?.uptime_seconds != null
                  ? `Uptime: ${formatUptime(calculator.uptime_seconds)}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>

        {/* Prediction WebSocket */}
        <Grid item xs={12} sm={6} md={4}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Prediction WebSocket</ServiceName>
                {wsLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(predWs?.status, wsError)}
                    color={getStatusColor(predWs?.status, wsError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!wsLoading && !wsError && predWs && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>Active Opps</MetricLabel>
                    <MetricValue>
                      {(predWs.activeOpportunities ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Messages Received</MetricLabel>
                    <MetricValue>
                      {(predWs.totalMessagesReceived ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Last Update Age</MetricLabel>
                    <MetricValue>
                      {predWs.lastUpdateAge != null
                        ? formatDuration(predWs.lastUpdateAge)
                        : "-"}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {wsError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {predWs?.uptime != null
                  ? `Uptime: ${formatUptime(predWs.uptime)}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>
      </Grid>
    </PageWithTitle>
  );
};

const ServiceCard = styled(Card)(({ theme }: any) => ({
  background: theme.palette.background.paper,
  borderRadius: "12px",
  height: "100%",
}));

const ServiceHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
});

const ServiceName = styled(Typography)(({ theme }: any) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const MetricsGrid = styled(Box)({
  display: "flex",
  flexWrap: "wrap" as const,
  gap: "1rem",
  marginBottom: "0.75rem",
});

const MetricItem = styled(Box)({
  minWidth: "80px",
});

const MetricLabel = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
}));

const MetricValue = styled(Typography)(({ theme }: any) => ({
  fontSize: "1.1rem",
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const ErrorText = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.85rem",
  color: theme.palette.error.main,
  marginBottom: "0.75rem",
}));

const LastChecked = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
  marginTop: "0.5rem",
}));
